import { lighten } from 'polished';
import styled from 'styled-components';
import { IDatePickerProps } from './DateRangePicker';

type IStyledDatePickerProps = Pick<IDatePickerProps, 'isMini' | 'width' | 'isBold'>;

// This CSS has been copied from the React Dates repo
// The CSS in here will only cover SingleDatePicker & DateRangePicker

export const StyledDatePicker = styled.div<IStyledDatePickerProps>`
  display: inline-block;

  .SingleDatePicker {
    display: inline-block;
    position: relative;

    &__block {
      display: block;
    }
    &_picker {
      z-index: 1;
      background-color: #fff;
      position: absolute;
    }
    &_picker__rtl {
      direction: rtl;
    }
    &_picker__directionLeft {
      left: 0;
    }
    &_picker__directionRight {
      right: 0;
    }
    &_picker__portal {
      background-color: rgba(0, 0, 0, 0.3);
      position: fixed;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
    }
    &_picker__fullScreenPortal {
      background-color: #fff;
    }
  }

  .SingleDatePickerInput {
    align-items: center;
    background: ${({ isMini, theme }) => (isMini ? 'transparent' : theme.vars.newColor.white)};
    display: inline-flex;

    &_calendarIcon,
    &_clearDate {
      align-items: center;
      background: transparent;
      border: 0;
      color: inherit;
      display: inline-flex;
      font: inherit;
      height: 28px;
      justify-content: center;
      line-height: normal;
      overflow: visible;
      cursor: pointer;
      padding: 0;
      margin: 0 10px 0 0;
      width: 30px;

      :hover {
        svg {
          color: ${({ theme }) => theme.vars.newColor.darkGS2};
        }
      }
      &__default {
        :hover,
        :focus {
          background: ${({ theme }) => theme.vars.newColor.lightGS1};
          border-radius: 50%;
        }
      }
      &__hide {
        visibility: hidden;
      }
      svg {
        color: ${({ theme }) => theme.vars.newColor.darkGS3};
      }
    }

    &_calendarIcon {
      svg {
        color: ${({ theme }) => theme.vars.newColor.darkGS2};
      }
    }

    &__withBorder {
      border: 1px solid ${({ isMini, theme }) => (isMini ? 'transparent' : theme.vars.newColor.lightGS1)};
    }
    &__rtl {
      direction: rtl;
    }

    &__disabled {
      background-color: ${({ isMini, theme }) => (isMini ? null : theme.vars.newColor.lightGS2)};
    }

    .SingleDatePickerInput {
      &__disabled {
        background-color: ${({ theme }) => theme.vars.newColor.lightGS2};
      }
    }
  }

  .DayPickerKeyboardShortcuts {
    &_buttonReset {
      background: 0 0;
      border: 0;
      border-radius: 0;
      color: inherit;
      font: inherit;
      line-height: normal;
      overflow: visible;
      padding: 0;
      cursor: pointer;
      font-size: 14px;
      :active {
        outline: 0;
      }
    }
    &_show {
      width: 33px;
      height: 26px;
      position: absolute;
      z-index: 2;
      ::before {
        content: '';
        display: block;
        position: absolute;
      }
      &__bottomRight {
        bottom: 0;
        right: 0;
        :before {
          border-top: 26px solid transparent;
          border-right: 33px solid ${({ theme }) => theme.vars.newColor.blue2};
          bottom: 0;
          right: 0;
        }
        :hover::before {
          border-right: 33px solid ${({ theme }) => theme.vars.newColor.blue2};
        }
      }
      &__topRight {
        top: 0;
        right: 0;
        ::before {
          border-bottom: 26px solid transparent;
          border-right: 33px solid ${({ theme }) => theme.vars.newColor.blue2};
          top: 0;
          right: 0;
        }

        :hover::before {
          border-right: 33px solid ${({ theme }) => theme.vars.newColor.blue2};
        }
      }
      &__topLeft {
        top: 0;
        left: 0;
        ::before {
          border-bottom: 26px solid transparent;
          border-left: 33px solid ${({ theme }) => theme.vars.newColor.blue2};
          top: 0;
          left: 0;
        }
        :hover::before {
          border-left: 33px solid ${({ theme }) => theme.vars.newColor.blue2};
        }
      }
    }
    &_showSpan {
      color: #fff;
      position: absolute;
    }
    &_showSpan__bottomRight {
      bottom: 0;
      right: 5px;
    }
    &_showSpan__topRight {
      top: 1px;
      right: 5px;
    }
    &_showSpan__topLeft {
      top: 1px;
      left: 5px;
    }
    &_panel {
      overflow: auto;
      background: #fff;
      border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
      border-radius: 2px;
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      z-index: 2;
      padding: 22px;
      margin: 33px;
      text-align: left;
    }
    &_title {
      font-size: 16px;
      font-weight: 700;
      margin: 0;
    }
    &_list {
      list-style: none;
      padding: 0;
      font-size: 14px;

      li {
        display: flex;
        align-items: center;
        margin-bottom: 5px;

        .KeyboardShortcutRow_keyContainer {
          padding: 2px 4px;
          margin-right: 10px;
          background-color: ${({ theme }) => theme.vars.newColor.lightGS2};
        }
      }
    }
    &_close {
      position: absolute;
      right: 22px;
      top: 22px;
      z-index: 2;
      :active {
        outline: 0;
      }
    }
    &_closeSvg {
      height: 15px;
      width: 15px;
      fill: ${({ theme }) => lighten(0.1, theme.vars.newColor.darkGS3)};
      :hover,
      :focus {
        fill: ${({ theme }) => theme.vars.newColor.darkGS3};
      }
    }
  }

  .CalendarDay {
    box-sizing: border-box;
    cursor: pointer;
    font-size: 14px;
    text-align: center;

    :focus {
      border: 1px double ${({ theme }) => theme.vars.newColor.blue2};
    }

    :active {
      outline: 0;
    }
    &__defaultCursor {
      cursor: default;
    }
    &__default {
      border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
      color: ${({ theme }) => theme.vars.newColor.darkGS1};
      background: #fff;
      :hover {
        background: ${({ theme }) => theme.vars.newColor.white};
        color: ${({ theme }) => theme.vars.newColor.black};
        border: 1px double ${({ theme }) => theme.vars.newColor.blue2};
      }
    }
    &__hovered_offset {
      background: #f4f5f5;
      border: 1px double ${({ theme }) => theme.vars.newColor.lightGS1};
      color: inherit;
    }
    &__outside {
      border: 0;
      background: #fff;
      color: ${({ theme }) => theme.vars.newColor.darkGS1};
      :hover {
        border: 0;
      }
    }
    &__blocked_minimum_nights {
      background: #fff;
      border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
      color: ${({ theme }) => lighten(0.1, theme.vars.newColor.darkGS3)};
      :active,
      :hover {
        background: #fff;
        color: ${({ theme }) => lighten(0.1, theme.vars.newColor.darkGS3)};
      }
    }
    &__highlighted_calendar {
      background: #ffe8bc;
      color: ${({ theme }) => theme.vars.newColor.darkGS1};
      :active,
      :hover {
        background: #ffce71;
        color: ${({ theme }) => theme.vars.newColor.darkGS1};
      }
    }
    &__selected_span {
      background: ${({ theme }) => lighten(0.45, theme.vars.newColor.blue2)};
      color: ${({ theme }) => theme.vars.newColor.black};
      :active,
      :hover {
        background: ${({ theme }) => theme.vars.newColor.white};
        color: ${({ theme }) => theme.vars.newColor.black};
        border: 1px double ${({ theme }) => theme.vars.newColor.blue2};
      }
    }
    &__selected {
      background: ${({ theme }) => theme.vars.newColor.blue2};
      color: ${({ theme }) => theme.vars.newColor.white};
    }
    &__hovered_span {
      background: ${({ theme }) => lighten(0.45, theme.vars.newColor.blue2)};
      color: ${({ theme }) => theme.vars.newColor.black};
    }
    &__selected:active,
    &__selected:hover,
    &__hovered_span:hover,
    &__hovered_span:active {
      background: ${({ theme }) => theme.vars.newColor.white};
      color: ${({ theme }) => theme.vars.newColor.black};
      border: 1px double ${({ theme }) => theme.vars.newColor.blue2};
    }
    &__blocked_calendar,
    &__blocked_calendar:active,
    &__blocked_calendar:hover {
      background: ${({ theme }) => lighten(0.1, theme.vars.newColor.darkGS3)};
      border: 1px solid ${({ theme }) => lighten(0.1, theme.vars.newColor.darkGS3)};
      color: ${({ theme }) => theme.vars.newColor.darkGS3};
    }
    &__blocked_out_of_range,
    &__blocked_out_of_range:active,
    &__blocked_out_of_range:hover {
      background: #fff;
      border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
      color: ${({ theme }) => lighten(0.1, theme.vars.newColor.darkGS3)};
    }
    &__hovered_start_first_possible_end {
      background: ${({ theme }) => theme.vars.newColor.lightGS1};
      border: 1px double ${({ theme }) => theme.vars.newColor.lightGS1};
    }
    &__hovered_start_blocked_min_nights {
      background: ${({ theme }) => theme.vars.newColor.lightGS1};
      border: 1px double ${({ theme }) => theme.vars.newColor.lightGS1};
    }
  }

  .CalendarMonth {
    background: #fff;
    text-align: center;
    vertical-align: top;
    user-select: none;
    &_table {
      border-collapse: collapse;
      border-spacing: 0;
    }
    &_verticalSpacing {
      border-collapse: separate;
    }
    &_caption {
      color: ${({ theme }) => theme.vars.newColor.darkGS1};
      font-size: 18px;
      text-align: center;
      padding-top: 22px;
      padding-bottom: 37px;
      caption-side: initial;
    }
    &_caption__verticalScrollable {
      padding-top: 12px;
      padding-bottom: 7px;
    }
  }

  .CalendarMonthGrid {
    background: #fff;
    text-align: left;
    z-index: 0;
    &__animating {
      z-index: 1;
    }
    &__horizontal {
      position: absolute;
      left: 9px;
    }
    &__vertical {
      margin: 0 auto;
    }
    &__vertical_scrollable {
      margin: 0 auto;
      overflow-y: scroll;
    }
    &_month__horizontal {
      display: inline-block;
      vertical-align: top;
      min-height: 100%;
    }
    &_month__hideForAnimation {
      position: absolute;
      z-index: -1;
      opacity: 0;
      pointer-events: none;
    }
    &_month__hidden {
      visibility: hidden;
    }
  }

  .DayPickerNavigation {
    position: relative;
    z-index: 2;
    &__horizontal {
      height: 0;
    }
    &__verticalDefault {
      position: absolute;
      width: 100%;
      height: 52px;
      bottom: 0;
      left: 0;
    }
    &__verticalScrollableDefault {
      position: relative;
    }
    &_button {
      cursor: pointer;
      user-select: none;
      border: 0;
      padding: 0;
      margin: 0;
    }
    &_button__default {
      border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
      background-color: #fff;
      color: #757575;
      :focus,
      :hover {
        border: 1px solid ${({ theme }) => theme.vars.newColor.darkGS3};
      }
      :active {
        background: ${({ theme }) => theme.vars.newColor.lightGS2};
      }
    }
    &_button__disabled {
      cursor: default;
      border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS2};
      :focus,
      :hover {
        border: 1px solid ${({ theme }) => theme.vars.newColor.lightGS2};
      }
      :active {
        background: 0 0;
      }
    }
    &_button__horizontalDefault {
      position: absolute;
      top: 18px;
      line-height: 0.78;
      border-radius: 3px;
      padding: 6px 9px;
    }
    &_leftButton__horizontalDefault {
      left: 22px;
    }
    &_rightButton__horizontalDefault {
      right: 22px;
    }
    &_button__verticalDefault {
      padding: 5px;
      background: #fff;
      box-shadow: 0 0 5px 2px rgba(0, 0, 0, 0.1);
      position: relative;
      display: inline-block;
      text-align: center;
      height: 100%;
      width: 50%;
    }
    &_nextButton__verticalDefault {
      border-left: 0;
    }
    &_nextButton__verticalScrollableDefault {
      width: 100%;
    }
    &_svg__horizontal {
      height: 19px;
      width: 19px;
      fill: ${({ theme }) => theme.vars.newColor.darkGS2};
      display: block;
    }
    &_svg__vertical {
      height: 42px;
      width: 42px;
      fill: ${({ theme }) => theme.vars.newColor.darkGS1};
    }
    &_svg__disabled {
      fill: ${({ theme }) => theme.vars.newColor.lightGS2};
    }
  }

  .DayPicker {
    background: #fff;
    position: relative;
    text-align: left;
    &__horizontal {
      background: #fff;
    }
    &__verticalScrollable {
      height: 100%;
    }
    &__hidden {
      visibility: hidden;
    }
    &__withBorder {
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05), 0 0 0 1px ${({ theme }) => theme.vars.newColor.lightGS1};
      border-radius: ${({ theme }) => theme.vars.borderRadius.default};
    }
    &_portal__horizontal {
      box-shadow: none;
      position: absolute;
      left: 50%;
      top: 50%;
    }
    &_portal__vertical {
      position: initial;
    }
    &_focusRegion {
      outline: 0;
    }
    &_calendarInfo__horizontal,
    &_wrapper__horizontal {
      display: inline-block;
      vertical-align: top;
    }
    &_weekHeaders {
      position: relative;
    }
    &_weekHeaders__horizontal {
      margin-left: 9px;
    }
    &_weekHeader {
      color: ${({ theme }) => theme.vars.newColor.darkGS2};
      position: absolute;
      top: 62px;
      z-index: 2;
      text-align: left;
    }
    &_weekHeader__vertical {
      left: 50%;
    }
    &_weekHeader__verticalScrollable {
      top: 0;
      display: table-row;
      border-bottom: 1px solid ${({ theme }) => theme.vars.newColor.lightGS1};
      background: #fff;
      margin-left: 0;
      left: 0;
      width: 100%;
      text-align: center;
    }
    &_weekHeader_ul {
      list-style: none;
      margin: 1px 0;
      padding-left: 0;
      padding-right: 0;
      font-size: 14px;
    }
    &_weekHeader_li {
      display: inline-block;
      text-align: center;
    }
    &_transitionContainer {
      position: relative;
      overflow: hidden;
      border-radius: 3px;
    }
    &_transitionContainer__horizontal {
      transition: height 0.2s ease-in-out;
    }
    &_transitionContainer__vertical {
      width: 100%;
    }
    &_transitionContainer__verticalScrollable {
      padding-top: 20px;
      height: 100%;
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      overflow-y: scroll;
    }
  }

  .DateInput {
    margin: 0;
    padding: 0;
    background: ${({ isMini, theme }) => (isMini ? 'transparent' : theme.vars.newColor.lightGS2)};
    position: relative;
    display: inline-block;
    width: 120px;
    width: ${({ width }) => (width ? width : 120)}px;
    vertical-align: middle;

    &__small {
      font-weight: ${({ isBold }) => (isBold ? '600' : null)};
      width: ${({ width }) => (width ? width : 85)}px;
    }

    &__disabled {
      background: ${({ isMini, theme }) => (isMini ? 'transparent' : theme.vars.newColor.lightGS2)};
      color: ${({ theme }) => theme.vars.newColor.lightGS1};
    }

    &_input {
      ${({ isMini, theme }) => (isMini ? null : theme.partials.newForm.item.default)};
      background-color: ${({ theme }) => theme.vars.newColor.white};
      border: none;
      min-height: ${({ isMini, theme }) => (isMini ? null : '48px')};
    }

    &_input__small {
      background: transparent;
      border: 0;
      color: ${({ theme }) => theme.vars.newColor.blue2};
      cursor: pointer;
      font-size: 14px;
      line-height: 18px;
      margin: 0;
      padding: 5px 5px 5px 0;
      width: 100%;

      ::placeholder {
        color: ${({ theme }) => theme.vars.newColor.blue2};
      }
    }

    &_input__regular {
      font-weight: auto;
    }
    &_input__readOnly {
      user-select: none;
    }
    &_input__disabled {
      background-color: ${({ isMini, theme }) => (isMini ? null : theme.vars.newColor.lightGS2)};
      color: ${({ theme }) => theme.vars.newColor.darkGS3};

      ::placeholder {
        color: ${({ theme }) => theme.vars.newColor.darkGS3};
      }
    }
    &_screenReaderMessage {
      border: 0;
      clip: rect(0, 0, 0, 0);
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      width: 1px;
    }
    &_fang {
      position: absolute;
      width: 20px;
      height: 10px;
      left: 22px;
      z-index: 2;
    }
    &_fangShape {
      fill: #fff;
    }
    &_fangStroke {
      stroke: ${({ theme }) => theme.vars.newColor.lightGS1};
      fill: transparent;
    }
  }
  .DateRangePickerInput {
    align-items: center;
    background: ${({ isMini, theme }) => (isMini ? 'transparent' : theme.vars.newColor.white)};
    display: inline-flex;

    &__disabled {
      background-color: ${({ isMini, theme }) => (isMini ? null : theme.vars.newColor.lightGS2)};

      .DateRangePickerInput_arrow {
        opacity: 0.2;
      }
    }
    &__withBorder {
      border: 1px solid ${({ isMini, theme }) => (isMini ? 'transparent' : theme.vars.newColor.lightGS1)};
    }
    &__rtl {
      direction: rtl;
    }
    &__block {
      display: block;
    }
    &_arrow {
      display: inline-block;
      vertical-align: middle;
      color: ${({ theme }) => theme.vars.newColor.darkGS1};
      margin: ${({ isMini }) => (isMini ? '0 5px' : null)};
    }
    &_arrow_svg {
      vertical-align: middle;
      fill: ${({ theme }) => theme.vars.newColor.darkGS1};
      height: 24px;
      width: 24px;
    }

    &_calendarIcon,
    &_clearDates {
      align-items: center;
      background: transparent;
      border: 0;
      color: inherit;
      display: inline-flex;
      font: inherit;
      height: 28px;
      justify-content: center;
      line-height: normal;
      overflow: visible;
      cursor: pointer;
      padding: 0;
      margin: 0 10px 0 0;
      width: 30px;

      :hover {
        svg {
          color: ${({ theme }) => theme.vars.newColor.darkGS2};
        }
      }
      &__default {
        :hover,
        :focus {
          background: ${({ theme }) => theme.vars.newColor.lightGS1};
          border-radius: 50%;
        }
      }
      &__hide {
        visibility: hidden;
      }
      svg {
        color: ${({ theme }) => theme.vars.newColor.darkGS3};
      }
    }

    &_calendarIcon {
      svg {
        color: ${({ theme }) => theme.vars.newColor.darkGS2};
      }
    }
  }

  .DateRangePicker {
    position: relative;
    display: inline-block;

    &__block {
      display: block;
    }
    &_picker {
      z-index: 1;
      background-color: #fff;
      position: absolute;
    }
    &_picker__rtl {
      direction: rtl;
    }
    &_picker__directionLeft {
      left: 0;
    }
    &_picker__directionRight {
      right: 0;
    }
    &_picker__portal {
      background-color: rgba(0, 0, 0, 0.3);
      position: fixed;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
    }
    &_picker__fullScreenPortal {
      background-color: #fff;
    }
    &_closeButton {
      background: 0 0;
      border: 0;
      color: inherit;
      font: inherit;
      line-height: normal;
      overflow: visible;
      cursor: pointer;
      position: absolute;
      top: 0;
      right: 0;
      padding: 15px;
      z-index: 2;
    }
  }
`;
